import React from "react";
import styled, { keyframes } from "styled-components";
import { Formik } from "formik";
import {
  Modal,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { CustomFileUpload } from "../../common/FormInputs";
import AWSService from "../../../services/AWSService";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage, getFileName } from "../../../helpers/functions";
import BundleService from "../../../services/BundleService";
import axios from "axios";
import COLORS from "../../../assets/Colors";

const BRAND = {
  navy: "#293461",
  teal: "#00838c",
  muted: "#949ab0",
  border: "#e8eaf0",
  surface: "#f7f8fc",
  white: "#ffffff",
};

const popIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const validFormat = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

const useStyles = makeStyles(() => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: BRAND.navy,
    fontSize: "14px",
  },
}));

const inputs = [
  { label: "(8 × 2)", name: "a4st16", value: "a4st16" },
  { label: "(6 × 2)", name: "a4st12", value: "a4st12" },
  { label: "(8 × 2) V2", name: "a4st18", value: "a4st18" },
];

const AddressLabel = ({ addressLabel, setAddressLabel }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  async function s3Upload(file, field, setFieldValue, setFieldError) {
    if (!file) return;
    try {
      if (!validFormat.includes(file?.type))
        enqueueSnackbar("Invalid File Type", { variant: "error" });
      setLoader({ state: true, message: `Uploading file: ${file.name} …` });
      const s3Response = await AWSService.getS3URL({ key: file.name });
      if (s3Response.url) {
        const res = await AWSService.uploadToS3(s3Response.url, file);
        setFieldValue(field, res);
        enqueueSnackbar("Successfully Uploaded", { variant: "success" });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      setFieldError(field, "Could not upload this file");
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const onFormSubmit = async (values) => {
    setLoader({ state: true, message: "Please wait…" });
    try {
      const { addressLabelType, url } = values;
      const isA4Type = ["a4st16", "a4st12"].includes(addressLabelType);
      let s3Url;

      if (isA4Type) {
        const payload = {
          documentsDet: {
            excelFile: url,
            addressLabelType,
            excelFileName: getFileName(url),
          },
        };
        const { data } = await axios.post(
          "https://staging-api.justact.co.in/addressLabel",
          payload,
          { headers: { "Content-Type": "application/json" } },
        );
        s3Url = data?.s3_file_url;
      } else {
        const response = await BundleService.createAddressLabel({
          s3_file_url: url,
        });
        s3Url = response?.s3_file_url;
      }

      if (s3Url) {
        const link = document.createElement("a");
        link.href = s3Url;
        link.download = getFileName(url);
        document.body.appendChild(link);
        link.click();
        link.remove();
        enqueueSnackbar("Address label generated successfully.", {
          variant: "success",
        });
      } else {
        throw new Error("Failed to generate address label.");
      }
    } catch (error) {
      console.error("Error generating address label:", error);
      enqueueSnackbar(error?.message || "Something went wrong.", {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setAddressLabel(false);
    }
  };

  return (
    <Modal open={addressLabel} onClose={() => setAddressLabel(false)}>
      <ModalOuter>
        <ModalCard>
          {/* Header */}
          <ModalHeader>
            <HeaderGroup>
              <ModalTitle>Address Label</ModalTitle>
            </HeaderGroup>
            <CloseBtn onClick={() => setAddressLabel(false)}>✕</CloseBtn>
          </ModalHeader>

          <Divider />

          {/* Form */}
          <Formik
            initialValues={{ url: "", addressLabelType: "a4st16" }}
            enableReinitialize
            validateOnBlur
            validateOnChange
            onSubmit={onFormSubmit}
          >
            {({
              values,
              handleChange,
              handleSubmit,
              setFieldValue,
              setFieldError,
            }) => (
              <ModalBody>
                <FieldSection>
                  <FieldLabel>Label Format</FieldLabel>
                  <Grid item xs={12}>
                    <RadioGroup
                      style={{ flexDirection: "row", gap: "8px" }}
                      name="addressLabelType"
                      value={values.addressLabelType}
                      onChange={handleChange}
                    >
                      {inputs.map((radio, key) => (
                        <RadioCard
                          key={key}
                          selected={values.addressLabelType === radio.value}
                        >
                          <FormControlLabel
                            classes={{ label: classes.formLabel }}
                            value={radio.value}
                            control={
                              <Radio
                                size="small"
                                style={{ color: BRAND.teal }}
                              />
                            }
                            label={radio.label}
                            style={{ margin: 0 }}
                          />
                        </RadioCard>
                      ))}
                    </RadioGroup>
                  </Grid>
                </FieldSection>

                <FieldSection>
                  <FieldLabel>Upload Excel File</FieldLabel>
                  <CustomFileUpload
                    setFile={(file) =>
                      s3Upload(file, "url", setFieldValue, setFieldError)
                    }
                    style={{ width: "100%", marginTop: "4px" }}
                    accept=".xlsx"
                    file={{ name: getFileName(values?.url) }}
                    fileUrl={values?.url}
                  />
                  <TemplateLink
                    onClick={() =>
                      window.open(
                        "https://justact-api-uploads.s3.ap-south-1.amazonaws.com/15/1729664876062-d54c43c7-c768-4740-8b43-32062d65aafd/Address_lable_Template.xlsx",
                      )
                    }
                  >
                    Download Address Label Template
                  </TemplateLink>
                </FieldSection>

                <Divider />

                <ModalFooter>
                  <CancelButton onClick={() => setAddressLabel(false)}>
                    Cancel
                  </CancelButton>
                  <SubmitButton disabled={!values.url} onClick={handleSubmit}>
                    Generate Label
                  </SubmitButton>
                </ModalFooter>
              </ModalBody>
            )}
          </Formik>
        </ModalCard>
      </ModalOuter>
    </Modal>
  );
};

export default AddressLabel;

const ModalOuter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 520px;
  animation: ${popIn} 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  outline: none;

  @media (max-width: 480px) {
    width: calc(100% - 16px);
  }
`;

const ModalCard = styled.div`
  background: ${BRAND.white};
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 20px 60px rgba(41, 52, 97, 0.18),
    0 4px 16px rgba(0, 131, 140, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
`;

const HeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: ${BRAND.navy};
  font-family: openSans-SemiBold, sans-serif;
`;

const CloseBtn = styled.button`
  background: rgba(41, 52, 97, 0.06);
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
  color: ${BRAND.navy};
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: rgba(41, 52, 97, 0.12);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${BRAND.border};
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${BRAND.muted};
`;

const RadioCard = styled.div`
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid
    ${({ selected }) => (selected ? "rgba(0,131,140,0.4)" : BRAND.border)};
  background: ${({ selected }) =>
    selected ? "rgba(0,131,140,0.05)" : BRAND.white};
  transition:
    border-color 0.2s,
    background 0.2s;
  cursor: pointer;

  &:hover {
    border-color: rgba(0, 131, 140, 0.35);
  }
`;

const TemplateLink = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: ${COLORS.BTN_GREEN};
  cursor: pointer;
  font-weight: 700;
  margin-top: 4px;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  background: ${COLORS.LOGOUT_RED};
  border: 1.5px solid ${BRAND.border};
  color: #ffffff;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${BRAND.teal} 0%, #005f66 100%);
  border: none;
  color: ${BRAND.white};
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 22px;
  border-radius: 10px;
  cursor: pointer;
  transition:
    opacity 0.2s,
    transform 0.15s;
  box-shadow: 0 4px 14px rgba(0, 131, 140, 0.3);

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  &:not(:disabled):hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;
